/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
* A small coloured pill as a field. It is a PicklistField underneath - the set of possible states
* IS picklist.items, each carrying a style, colour, optional icon and a tooltip - so column
* filtering and the analysis charts work with no special-casing (a pill is just a picklist that
* renders as a chip, never a <select>). The value is never edited here; a click runs one of three context
* callbacks (formClick / gridClick / tableClick), each handed the whole row object and returning
* an Observable of the updated object; the invoker applies that result to wherever the pill lives.
*
* Domain specifics (the states, the click flows) come from a factory - see requestPillFields.ts.
*
* Two ways to place it:
*   - control path (a form via <app-ctl-holder>, or a grid row): bind [control] + [on].
*   - raw path (a table cell, the related-requests simple list): bind [field] + [item] + on="grid".
*/
import { Component, EventEmitter, Input, Output, ViewContainerRef } from '@angular/core';
import { Observable } from 'rxjs';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AbstractObject } from '@src/app/model/abstract-object';
import { FieldType } from '@src/app/shared/field/Field';
import { PicklistField } from '@src/app/shared/field/PicklistField';
import { ControlOn, AppFormControl } from '@src/app/shared/form/app-form-control';
import { GridRow } from '@src/app/shared/grid/grid-row';
import { FormControlInterface } from '@src/app/shared/form/form-control-interface';

/** infill = colour behind its text colour (a state). outline = colour as text + border (an attribute). */
export type PillStyle = 'infill' | 'outline';

export interface PillColorDef {
    name: string;
    background: string;
    /** The text on an infill pill - an outline pill's text is its background colour */
    text: 'white' | 'black';
}

/*
 * Every colour a pill can be, in one place, so a new hue costs one entry here and nothing in the
 * SCSS - the component paints the element directly. The key is what a state names and what
 * forums.pillColor stores, so a key never changes - only what it paints.
 *
 * The light hues (black text) are infill only: an outline pill's colour IS its text, and these do
 * not read as text on a light page.
 */
export const PillColors = {
    neutral: { name: $localize`Neutral`, background: '#616161', text: 'white' },
    green: { name: $localize`Green`, background: '#2e7d32', text: 'white' },
    darkGreen: { name: $localize`Dark green`, background: '#1b5e20', text: 'white' },
    amber: { name: $localize`Amber`, background: '#e65100', text: 'white' },
    red: { name: $localize`Red`, background: '#c62828', text: 'white' },
    blue: { name: $localize`Blue`, background: '#1565c0', text: 'white' },
    grey: { name: $localize`Grey`, background: '#757575', text: 'white' },
    teal: { name: $localize`Teal`, background: '#008080', text: 'white' },
    darkBlue: { name: $localize`Dark blue`, background: '#00008b', text: 'white' },
    indigo: { name: $localize`Indigo`, background: '#4b0082', text: 'white' },
    purple: { name: $localize`Purple`, background: '#800080', text: 'white' },
    gold: { name: $localize`Gold`, background: '#ffd700', text: 'black' },
    yellow: { name: $localize`Yellow`, background: '#ffff00', text: 'black' },
    pink: { name: $localize`Pink`, background: '#ffc0cb', text: 'black' },
    orange: { name: $localize`Orange`, background: '#ffa500', text: 'black' },
    lightGrey: { name: $localize`Light grey`, background: '#d3d3d3', text: 'black' },
} as const satisfies Record<string, PillColorDef>;

/** Only a PillColors key compiles - a new hue is an entry above, never a hex at the call site. */
export type PillColor = keyof typeof PillColors;

/**
 * One selectable state of a pill - a picklist item plus how it should paint. Deliberately loose so
 * a domain enum list (e.g. ServiceRequest.srStates) can be handed straight in as `states`.
 * No `style` means no pill at all - the value renders as plain text.
 */
export interface PillState {
    id: string | number;
    name?: string;
    style?: PillStyle;
    color?: PillColor;
    icon?: string;
    tip?: string;
    /** Something is in progress - the pill glows until the value moves on. */
    working?: boolean;
}

/** Give a pill either a fixed set of states or makeOption, which builds each state from its row. */
export interface PillFieldOptions {
    states?: PillState[];
    makeOption?: (o: AbstractObject) => PillState;
    condensed?: boolean;
    tableIconOnly?: boolean;
    formClick?: (o: AbstractObject) => Observable<AbstractObject | null>;
    gridClick?: (o: AbstractObject) => Observable<AbstractObject | null>;
    tableClick?: (o: AbstractObject) => Observable<AbstractObject | null>;
    clickTip?: (o: AbstractObject, on: ControlOn) => string;
    rowTip?: (o: AbstractObject) => string;
    afterFormChange?: (o: AbstractObject) => void;
}

export class PillField extends PicklistField {
    type: FieldType = 'pill';

    tableIconOnly?: boolean;

    /** Dialog + branching only; each returns the updated object (null if the click was cancelled). */
    formClick?: (o: AbstractObject) => Observable<AbstractObject | null>;
    gridClick?: (o: AbstractObject) => Observable<AbstractObject | null>;
    tableClick?: (o: AbstractObject) => Observable<AbstractObject | null>;

    /** What a click will do for this row - added to the state's tip whenever the pill is clickable. */
    clickTip?: (o: AbstractObject, on: ControlOn) => string;

    /** A tip worked out from the row rather than fixed on the state - wins over the state's tip. */
    rowTip?: (o: AbstractObject) => string;

    /** Optional extra refresh after a form-mode click - grid rows refresh themselves. */
    afterFormChange?: (o: AbstractObject) => void;

    /*
     * Resolved once per row/instance in setValue: on a form or grid the component has only the
     * control at render time, not the item. The raw path passes the item straight in and calls
     * styleFor() / colorFor() / iconFor() / tipFor().
     */
    _item: AbstractObject;
    _name = '';
    _style: PillStyle = null;
    _color: PillColor = 'neutral';
    _icon = '';
    _tip = '';
    _working = false;

    /**
     * The underlying picklist configuration for a channel pill is usually
     * self populated on form / table pages, but to use them on other
     * components, e.g. related requests/we need to populate them directly
     */
    private stateFor(o: AbstractObject): PillState | undefined {
        const id = this.getValue(o);
        if (this.picklist.makeOption && id != null && id !== '') {
            return this.picklist.makeOption(o) as unknown as PillState;
        }
        return this.picklist.items.find(s => s.id === id) as PillState;
    }

    nameFor(o: AbstractObject): string {
        return this.stateFor(o)?.name ?? '';
    }

    styleFor(o: AbstractObject): PillStyle {
        return this.stateFor(o)?.style ?? null;
    }

    colorFor(o: AbstractObject): PillColor {
        return this.stateFor(o)?.color ?? 'neutral';
    }

    iconFor(o: AbstractObject): string {
        return this.stateFor(o)?.icon ?? '';
    }

    tipFor(o: AbstractObject): string {
        return (o && this.rowTip?.(o)) || (this.stateFor(o)?.tip ?? '');
    }

    workingFor(o: AbstractObject): boolean {
        return !!this.stateFor(o)?.working;
    }

    setValue(item: AbstractObject, readonly = false) {
        super.setValue(item, readonly);
        this._item = item;
        this._name = this.nameFor(item);
        this._style = this.styleFor(item);
        this._color = this.colorFor(item);
        this._icon = this.iconFor(item);
        this._tip = this.tipFor(item);
        this._working = this.workingFor(item);
    }
}

@Component({
    selector: 'app-form-pill',
    templateUrl: './form-pill.component.html',
    styleUrls: ['./form-pill.component.scss'],
    imports: [NgStyle, NgTemplateOutlet, MatIconModule, MatTooltipModule],
})
export class FormPillComponent implements FormControlInterface {
    @Input() control: AppFormControl;
    @Input() on: ControlOn = 'form';

    /** Raw path (no control) - table cells and the related-requests simple table. */
    @Input() field: PillField;
    @Input() item: AbstractObject;
    @Output() clicked = new EventEmitter<void>();

    get pillField(): PillField {
        return this.control ? this.control.field as PillField : this.field;
    }

    get text(): string {
        const name = this.control ? this.pillField._name : this.pillField?.nameFor(this.item);
        const raw = this.control ? this.control.value : this.pillField?.getValue(this.item);
        // formatValue maps id -> display name, or returns the id unchanged when there is no
        // matching state; fall back hard to the raw id so we never render an empty pill.
        return (name || this.pillField?.formatValue(raw) || raw || '') as string;
    }

    get pillStyle(): PillStyle {
        return this.control ? this.pillField._style : this.pillField.styleFor(this.item);
    }

    get icon(): string {
        return this.control ? this.pillField._icon : this.pillField.iconFor(this.item);
    }

    /** Table cell of a tableIconOnly field - the icon alone: reversed out of a disc if infill, else in its colour. */
    get iconOnly(): boolean {
        return !this.control && !!this.pillField?.tableIconOnly;
    }

    get color(): PillColor {
        return this.control ? this.pillField._color : this.pillField.colorFor(this.item);
    }

    /** No style - the value is unremarkable and renders as plain text rather than a chip. */
    get boxClass(): string {
        if (this.iconOnly) {
            return this.pillStyle === 'infill' ? 'pill-icon-only pill-icon-disc' : 'pill-icon-only';
        }
        const working = this.control ? this.pillField._working : this.pillField.workingFor(this.item);
        return (this.pillStyle ? 'pill' : 'pill-plain') + (working ? ' pill-working' : '');
    }

    /*
     * Painted here rather than by a colour class so a new hue needs no SCSS. Both styles set a
     * border - an infill pill borders in its own colour - so the two are the same size to the
     * pixel and line up in a condensed row.
     */
    get boxStyle(): Record<string, string> {
        const style = this.pillStyle;
        const paint = PillColors[this.color];
        const hue = paint.background;
        if (this.iconOnly && style !== 'infill') {
            return { color: hue };
        }
        if (!style) {
            return {};
        }
        // --pill-color carries the hue to the .pill-working glow, which cannot use currentColor
        // because an infill pill's text is not its hue.
        return style === 'outline'
            ? { color: hue, borderColor: hue, backgroundColor: 'transparent', '--pill-color': hue }
            : { color: paint.text, borderColor: hue, backgroundColor: hue, '--pill-color': hue };
    }

    /** An icon-only pill has no text, so the tooltip falls back to the state name. */
    get tipText(): string {
        const tip = this.control ? this.pillField._tip : this.pillField.tipFor(this.item);
        const stateTip = tip || (this.iconOnly ? this.text : '');
        const row = this.control ? this.pillField._item : this.item;
        const clickTip = this.canClick && row ? this.pillField.clickTip?.(row, this.on) : '';
        return [stateTip, clickTip].filter(Boolean).join(' - ');
    }

    get canClick(): boolean {
        const f = this.pillField;
        if (!f) {
            return false;
        }
        if (this.control) {
            return this.on === 'grid' ? !!f.gridClick : !!f.formClick;
        }
        return !!(f.tableClick || f.gridClick);
    }

    onClick(event: Event) {
        event.stopPropagation();
        if (!this.control) {
            this.clicked.emit();
            return;
        }
        const f = this.pillField;
        const flow = this.on === 'grid' ? f.gridClick : f.formClick;
        flow?.(f._item).subscribe(updated => {
            if (!updated) {
                return;
            }
            if (this.on === 'grid') {
                (this.control.getRow() as GridRow).refreshFrom(updated);
            } else {
                Object.assign(f._item, updated);
                f.afterFormChange?.(updated);
            }
        });
    }

    static make(label: string, value: string, o: PillFieldOptions): PillField {
        const overrides: Partial<PillField> = {
            condensed: o.condensed ?? true,
            tableIconOnly: o.tableIconOnly ?? false,
            sendServer: false,
            formClick: o.formClick,
            gridClick: o.gridClick,
            tableClick: o.tableClick,
            clickTip: o.clickTip,
            rowTip: o.rowTip,
            afterFormChange: o.afterFormChange,
        };
        const field = new PillField({ label, value }, overrides);
        field.picklist.items = (o.states ?? []) as unknown as AbstractObject[];
        if (o.makeOption) {
            field.picklist.selfPopulate = true;
            field.picklist.makeOption = o.makeOption as unknown as (item: AbstractObject) => AbstractObject;
        }
        field.picklist.blockFilterNone = true;
        field.formControlFactory = FormPillComponent.createComponent;
        // width: 1% shrinks the column to its content in a 100%-wide table; equal padding centres it.
        if (field.tableIconOnly) {
            field.cellStyle('text-align: center; padding-left: 4px; padding-right: 4px; width: 1%');
            field.cellOpts.thStyle = 'padding-left: 4px; padding-right: 4px; width: 1%';
        }
        return field;
    }

    static createComponent(vcr: ViewContainerRef, ctl: AppFormControl, on: ControlOn) {
        const componentRef = vcr.createComponent(FormPillComponent);
        componentRef.instance.control = ctl;
        componentRef.instance.on = on;
        return componentRef;
    }
}
